import React, { useEffect, useMemo, useState } from "react";
import { TEMPLATES, generateNextRuleSetCode } from "./engine";
import {
  Btn, CardButton, Check, ChipToggle, DateInput, Field, Input, Modal, RuleTypeChip, TextArea, C,
} from "./ui";
import SvgIcon from "../ipd-billing/SvgIcon";

const FALLBACK_DOMAIN_PRESETS = [
  { key: "IPD Billing", hint: "In-patient ward, ICU, procedures", code_prefix: "RS-IPD-" },
  { key: "OPD Billing", hint: "Out-patient consults, day-care", code_prefix: "RS-OPD-" },
  { key: "Insurance", hint: "TPA, cashless, pre-auth tariffs", code_prefix: "RS-TPA-" },
  { key: "Corporate", hint: "Corporate tie-up & MoU rates", code_prefix: "RS-CORP-" },
  { key: "Pharmacy", hint: "Drug pricing, MRP, GST slabs", code_prefix: "RS-PH-" },
  { key: "Emergency", hint: "After-hours surcharge, triage", code_prefix: "RS-ER-" },
  { key: "Doctor Payout", hint: "Doctor commission / share per service", code_prefix: "RS-DOCPAY-" },
];

const FALLBACK_ENC = ["OPD", "IPD", "EMERGENCY", "DAYCARE"];
const FALLBACK_PAY = ["SELF", "TPA", "INSURANCE", "CORPORATE"];

const emptyDraft = (year) => ({
  code: "", name: "", domain: "IPD Billing", description: "",
  priority: 1, effective_from: `${year}-04-01`, effective_to: "",
  active: true, encounterTypes: ["IPD"], payerTypes: ["TPA", "SELF"],
  templates: ["icu-tpa"],
});

export default function NewRuleSetWizard({ open, onClose, existingCodes, catalog, creating, onCreate }) {
  const year = new Date().getFullYear();
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState(() => emptyDraft(year));

  const domainPresets = useMemo(() => {
    const fromApi = catalog?.domain_presets;
    if (fromApi?.length) {
      return fromApi.map((p) => ({
        key: p.key,
        hint: p.hint ?? p.rule_domain ?? "",
        code: p.code_prefix ?? p.code ?? `RS-`,
      }));
    }
    return FALLBACK_DOMAIN_PRESETS.map((p) => ({ key: p.key, hint: p.hint, code: p.code_prefix }));
  }, [catalog]);

  const encTypes = catalog?.encounter_types?.length ? catalog.encounter_types : FALLBACK_ENC;
  const payTypes = catalog?.payer_types?.length ? catalog.payer_types : FALLBACK_PAY;
  const starterTemplates = catalog?.starter_templates?.length ? catalog.starter_templates : TEMPLATES;

  const codeForDomain = (domainKey) => {
    const preset = domainPresets.find((p) => p.key === domainKey) ?? domainPresets[0];
    if (!preset) return "";
    return generateNextRuleSetCode(preset.code, year, existingCodes);
  };

  useEffect(() => {
    if (!open) return;
    setDraft((d) => {
      const domain = d.domain || domainPresets[0]?.key || "IPD Billing";
      const nextCode = codeForDomain(domain);
      return {
        ...d,
        domain,
        code: nextCode,
        name: d.name || `${domain} Rules ${year}`,
      };
    });
  }, [open, draft.domain, existingCodes, domainPresets, year]);

  const reset = () => {
    setStep(0);
    setDraft(emptyDraft(year));
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const update = (p) => setDraft((d) => ({ ...d, ...p }));
  const toggle = (key, v) =>
    setDraft((d) => ({
      ...d,
      [key]: d[key].includes(v) ? d[key].filter((x) => x !== v) : [...d[key], v],
    }));

  const pickDomain = (k) => {
    update({
      domain: k,
      code: codeForDomain(k),
      name: draft.name || `${k} Rules ${year}`,
    });
  };

  const codeTaken = draft.code && existingCodes.some(
    (c) => String(c).trim().toUpperCase() === String(draft.code).trim().toUpperCase(),
  );
  const stepErrors = [
    [
      ...(!draft.domain ? ["Pick a domain"] : []),
      ...(!draft.code ? ["Code is required"] : []),
      ...(!draft.name ? ["Name is required"] : []),
      ...(codeTaken ? ["Code already exists — close and reopen wizard to refresh"] : []),
    ],
    [
      ...(draft.encounterTypes.length === 0 ? ["Pick at least one encounter type"] : []),
      ...(draft.payerTypes.length === 0 ? ["Pick at least one payer type"] : []),
      ...(!draft.effective_from ? ["Effective from is required"] : []),
      ...(!Number.isFinite(draft.priority) ? ["Priority must be numeric"] : []),
    ],
    [],
  ];

  const conflicts = useMemo(() => {
    const out = [];
    if (draft.encounterTypes.includes("IPD") && draft.payerTypes.includes("TPA"))
      out.push("Overlaps with IPD/TPA tariffs — priority should be < 2 to take effect.");
    if (draft.effective_to && draft.effective_to < draft.effective_from)
      out.push("Effective end date is before start date.");
    return out;
  }, [draft, existingCodes]);

  const next = () => {
    if (stepErrors[step].length) return;
    setStep((s) => Math.min(2, s + 1));
  };

  const create = async () => {
    const errs = [...stepErrors[0], ...stepErrors[1]];
    if (errs.length) { setStep(0); return; }
    await onCreate(draft);
  };

  const steps = ["Basics", "Scope & Validity", "Starter Rules"];

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      wide
      title="New Rule Set"
      description="Group related billing rules under a single, versioned configuration."
      footer={(
        <>
          <Btn variant="ghost" onClick={handleClose}>Cancel</Btn>
          {step > 0 && <Btn variant="outline" onClick={() => setStep((s) => s - 1)}>Back</Btn>}
          {step < 2 ? (
            <Btn onClick={next} disabled={stepErrors[step].length > 0}>Next</Btn>
          ) : (
            <Btn onClick={create} disabled={creating}>
              <SvgIcon name="check" size={14} color="#fff" />
              {creating ? "Creating…" : "Create Rule Set"}
            </Btn>
          )}
        </>
      )}
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {steps.map((label, i) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <span style={{
              width: 24, height: 24, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, fontSize: 11,
              background: i < step ? C.success : i === step ? C.primary : "#e9ecef",
              color: i <= step ? "#fff" : C.textSub,
            }}>
              {i < step ? <SvgIcon name="check" size={12} color="#fff" /> : i + 1}
            </span>
            <span style={{ fontWeight: i === step ? 700 : 400, color: i === step ? C.text : C.textSub }}>{label}</span>
            {i < steps.length - 1 && <span style={{ color: C.border, margin: "0 4px" }}>›</span>}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Field label="Domain">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8, marginTop: 4 }}>
              {domainPresets.map((d) => (
                <CardButton key={d.key} selected={draft.domain === d.key} onClick={() => pickDomain(d.key)}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{d.key}</div>
                  <div style={{ fontSize: 11, color: C.textSub, marginTop: 4 }}>{d.hint}</div>
                </CardButton>
              ))}
            </div>
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Rule Set Code *">
              <Input
                value={draft.code}
                mono
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiOutlinedInput-root": { background: "#f8f9fa" } }}
              />
              <div style={{ fontSize: 11, color: C.textSub, marginTop: 4 }}>
                Auto-assigned next available code for this domain and year.
              </div>
            </Field>
            <Field label="Rule Set Name *">
              <Input value={draft.name} onChange={(e) => update({ name: e.target.value })} placeholder="IPD Billing Rules 2026" />
            </Field>
          </div>
          <Field label="Description">
            <TextArea value={draft.description} onChange={(e) => update({ description: e.target.value })} placeholder="What this rule set covers…" />
          </Field>
        </div>
      )}

      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Field label="Encounter scope">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
              {encTypes.map((t) => <ChipToggle key={t} active={draft.encounterTypes.includes(t)} onClick={() => toggle("encounterTypes", t)}>{t}</ChipToggle>)}
            </div>
          </Field>
          <Field label="Payer scope">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
              {payTypes.map((t) => <ChipToggle key={t} active={draft.payerTypes.includes(t)} onClick={() => toggle("payerTypes", t)}>{t}</ChipToggle>)}
            </div>
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <Field label="Priority *">
              <Input type="number" inputProps={{ min: 1 }} value={draft.priority} onChange={(e) => update({ priority: Number(e.target.value) })} />
            </Field>
            <Field label="Effective From *">
              <DateInput value={draft.effective_from} onChange={(v) => update({ effective_from: v })} required />
            </Field>
            <Field label="Effective To">
              <DateInput value={draft.effective_to} minDate={draft.effective_from} onChange={(v) => update({ effective_to: v })} />
            </Field>
          </div>
          <Check
            checked={draft.active}
            onChange={(e) => update({ active: e.target.checked })}
            label="Activate immediately on creation"
          />
          {conflicts.length > 0 && (
            <div style={{ padding: 12, borderRadius: 8, background: "#fff8e1", border: "1px solid #ffe69c", fontSize: 12 }}>
              <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <SvgIcon name="sparkle" size={14} color="#856404" /> AI conflict scan
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>{conflicts.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 12, color: C.textSub }}>Seed this rule set with starter rules. You can edit them after creation.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
            {starterTemplates.map((t) => (
              <CardButton key={t.key} selected={draft.templates.includes(t.key)} onClick={() => toggle("templates", t.key)}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
                <div style={{ marginTop: 6 }}><RuleTypeChip type={t.type} /></div>
              </CardButton>
            ))}
          </div>
          <div style={{ padding: 12, borderRadius: 8, background: "#f8f9fa", fontSize: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Review</div>
            <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "4px 12px" }}>
              <span style={{ color: C.textSub }}>Code</span><span style={{ fontFamily: "monospace" }}>{draft.code}</span>
              <span style={{ color: C.textSub }}>Name</span><span>{draft.name}</span>
              <span style={{ color: C.textSub }}>Domain</span><span>{draft.domain}</span>
              <span style={{ color: C.textSub }}>Starter rules</span><span>{draft.templates.length}</span>
            </div>
          </div>
        </div>
      )}

      {stepErrors[step].length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: C.danger }}>{stepErrors[step].join(" · ")}</div>
      )}
    </Modal>
  );
}
